


import { Component, Vue, Emit } from "vue-property-decorator";

import Tab from "Web/Components/Tabs/Tab.vue";

@Component
export default class Tabs extends Vue {
    private tabs: Tab[] = [];

    private activeTab: Tab = {} as Tab;

    /* Indices of tabs that overflow to the second line; rendered inside a dropdown trigger. */
    private overflowIndices: number[] = [];

    /* Toggle for the overflow dropdown menu visibility. */
    private overflowOpen: boolean = false;

    /* When true, force render of overflow trigger so we can measure with reserved width. */
    private measuring: boolean = false;

    /* Index of overflow tab promoted into the visible row in place of original last visible.
       Persists across navigation so the chosen overflow tab stays the visible anchor. */
    private swapIndex: number = -1;

    private resizeObserver: any = null;

    private outsideClickHandler: ( ( e: MouseEvent ) => void ) | null = null;

    private mounted() {
        /* Filter out any none Tab components since we depend on their props/data. */
        this.tabs = this.$children.filter( ( child: Vue ) => child instanceof Tab ) as Tab[];

        let defaultTab: Tab | undefined;
        let firstEnabledTab: Tab | undefined;

        /* Find a default tab if there is any, or the first available tab to be used as default
           in case the user didn't specify it explicitely. */
        for( const tab of this.tabs ) {
            if( tab.Default && defaultTab === undefined )
                defaultTab = tab;

            if( !tab.Disabled && firstEnabledTab === undefined )
                firstEnabledTab = tab;
        }

        if( defaultTab )
            this.select( defaultTab );

        else if( firstEnabledTab )
            this.select( firstEnabledTab );

        this.$nextTick( () => this.computeOverflow() );

        const RO = ( window as any ).ResizeObserver;
        if( typeof RO !== "undefined" ) {
            this.resizeObserver = new RO( () => this.computeOverflow() );
            const optionsEl = this.$el.querySelector( ".c-tabs__options" );
            if( optionsEl ) this.resizeObserver.observe( optionsEl );
        }

        this.outsideClickHandler = ( e: MouseEvent ) => {
            if( !this.overflowOpen ) return;
            const trigger = this.$el.querySelector( ".c-tabs__overflow-toggle" );
            if( trigger && trigger.contains( e.target as Node ) ) return;
            const activeTitle = this.$el.querySelector( ".c-tabs__tab-title--has-overflow" );
            if( activeTitle && activeTitle.contains( e.target as Node ) ) return;
            this.overflowOpen = false;
        };
        document.addEventListener( "click", this.outsideClickHandler );
    }

    private beforeDestroy() {
        if( this.resizeObserver ) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if( this.outsideClickHandler ) {
            document.removeEventListener( "click", this.outsideClickHandler );
            this.outsideClickHandler = null;
        }
    }

    private computeOverflow() {
        /* Reset + force trigger visible so its width is reserved during measurement. */
        this.measuring = true;
        this.overflowIndices = [];
        this.$nextTick( () => {
            const ul = this.$el.querySelector( ".c-tabs__options" ) as HTMLElement | null;
            if( !ul ) { this.measuring = false; return; }
            const items = ul.querySelectorAll( "li.c-tabs__tab-title:not(.c-tabs__overflow)" );
            if( items.length === 0 ) { this.measuring = false; return; }
            /* Skip overflow detection for vertically stacked tabs (sidebar layouts).
               In a vertical list every item has the same offsetLeft and a different
               offsetTop, which would otherwise be misread as horizontal wrap. */
            if( items.length > 1 ) {
                const first = items[ 0 ] as HTMLElement;
                const second = items[ 1 ] as HTMLElement;
                if( first.offsetLeft === second.offsetLeft
                    && second.offsetTop > first.offsetTop ) {
                    this.measuring = false;
                    return;
                }
            }
            const baseTop = ( items[ 0 ] as HTMLElement ).offsetTop;
            const overflow: number[] = [];
            for( let i = 0; i < items.length; i++ ) {
                const el = items[ i ] as HTMLElement;
                if( el.offsetTop > baseTop ) overflow.push( i );
            }
            this.overflowIndices = overflow;
            /* On first overflow detection, if an overflow tab is currently active,
               promote it as the swap anchor so it stays visible after navigation. */
            if( this.swapIndex === -1 ) {
                for( const i of overflow ) {
                    if( this.tabs[ i ] && this.tabs[ i ].Active ) { this.swapIndex = i; break; }
                }
            }
            /* Invalidate swap if no longer overflowing (window grew, indices changed). */
            if( this.swapIndex !== -1 && overflow.indexOf( this.swapIndex ) === -1 ) {
                this.swapIndex = -1;
            }
            this.measuring = false;
        } );
    }

    private get overflowActive(): boolean {
        for( const i of this.overflowIndices ) {
            if( this.tabs[ i ] && this.tabs[ i ].Active ) return true;
        }
        return false;
    }

    private onTabClick( tab: Tab, i: number ) {
        if( i === this.lastVisibleIndex && tab.Active && this.overflowIndices.length > 0 ) {
            this.overflowOpen = !this.overflowOpen;
            return;
        }
        this.select( tab );
    }

    private onOverflowSelect( i: number ) {
        const tab = this.tabs[ i ];
        if( !tab || tab.Disabled ) return;
        this.overflowOpen = false;
        /* Picking the displaced original last-visible resets swap (returns it to row). */
        if( i === this.originalLastVisibleIndex ) {
            this.swapIndex = -1;
        } else if( this.overflowIndices.indexOf( i ) !== -1 ) {
            this.swapIndex = i;
        }
        this.select( tab );
    }

    private isOverflow( i: number ): boolean {
        if( this.measuring ) return false;
        return this.effectiveOverflowIndices.indexOf( i ) >= 0;
    }

    private get swappedOverflowIndex(): number {
        if( this.measuring || this.overflowIndices.length === 0 ) return -1;
        if( this.swapIndex !== -1 && this.overflowIndices.indexOf( this.swapIndex ) !== -1 ) {
            return this.swapIndex;
        }
        for( const i of this.overflowIndices ) {
            if( this.tabs[ i ] && this.tabs[ i ].Active ) return i;
        }
        return -1;
    }

    private get originalLastVisibleIndex(): number {
        if( this.measuring || this.overflowIndices.length === 0 ) return -1;
        for( let i = this.tabs.length - 1; i >= 0; i-- ) {
            const tab = this.tabs[ i ];
            if( this.overflowIndices.indexOf( i ) === -1 && tab && tab.Visible ) {
                return i;
            }
        }
        return -1;
    }

    private get effectiveOverflowIndices(): number[] {
        const swap = this.swappedOverflowIndex;
        if( swap === -1 ) return this.overflowIndices;
        const orig = this.originalLastVisibleIndex;
        const arr: number[] = [];
        for( const i of this.overflowIndices ) {
            if( i === swap ) continue;
            arr.push( i );
        }
        if( orig !== -1 ) arr.push( orig );
        return arr;
    }

    private get lastVisibleIndex(): number {
        const swap = this.swappedOverflowIndex;
        if( swap !== -1 ) return swap;
        return this.originalLastVisibleIndex;
    }

    @Emit( "Select" )
    private select( tab: Tab ) {
        if( tab.Disabled )
            return undefined;

        this.activeTab.Active = false;

        tab.Active = true;

        this.activeTab = tab;
        return tab;
    }
}
